import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardCheck, Check, X } from 'lucide-react';
import * as aprobacionesApi from '@/api/aprobaciones.api';
import type { Aprobacion, EstadoAprobacion } from '@/api/aprobaciones.api';
import { ApiError } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { PageHeader } from '@/components/common/PageHeader';
import { useToast } from '@/hooks/useToast';

/**
 * BANDEJA DE APROBACIONES (estándar Codeus). Lo que alguien pidió pasando su tope
 * (un descuento grande, un precio a mano) espera aquí a quien aprueba en esa área.
 * Aprobar lo hace tal cual; rechazar lo deja anotado. Quien lo pidió ve en qué quedó.
 * Quién puede aprobar lo decide el SERVIDOR; esta pantalla solo pregunta.
 */
const ESTADO: Record<EstadoAprobacion, { texto: string; variante: 'warning' | 'success' | 'danger' | 'default' }> = {
  pendiente: { texto: 'Esperando', variante: 'warning' },
  aprobando: { texto: 'Aprobando…', variante: 'default' },
  aprobada: { texto: 'Aprobada', variante: 'success' },
  rechazada: { texto: 'Rechazada', variante: 'danger' },
  fallida: { texto: 'Aprobada, no se pudo hacer', variante: 'danger' },
  vencida: { texto: 'Venció sin respuesta', variante: 'default' },
  interrumpida: { texto: 'Se cortó a mitad: revisa si se hizo', variante: 'warning' },
};
const mensaje = (err: unknown, otro: string) => (err instanceof ApiError ? err.message : otro);
const fecha = (iso: string) => new Date(iso).toLocaleString('es-DO', { dateStyle: 'medium', timeStyle: 'short' });
const quien = (p: { name: string | null; email?: string } | null) => (p ? p.name || p.email || '' : '');

/** Lo que se pidió, dicho en una línea (para una venta: cuántas líneas y el descuento). */
function resumen(a: Aprobacion): string {
  const c = (a.cuerpoJson ?? {}) as { lineas?: unknown[]; descuento?: number };
  const lineas = Array.isArray(c.lineas) ? c.lineas.length : 0;
  const partes = [lineas ? `${lineas} línea${lineas === 1 ? '' : 's'}` : null, a.motivo ? `quiere ${a.motivo}` : `pide ${a.pedido} (su tope es ${a.limite})`];
  return partes.filter(Boolean).join(' · ');
}

/**
 * TODO lo que se va a hacer, no solo el número que pasó el tope: al aprobar se ejecuta el
 * pedido entero (líneas, precios a mano, envío, cliente), y quien firma tiene que verlo.
 */
function Detalle({ a }: { a: Aprobacion }) {
  const c = (a.cuerpoJson ?? {}) as Record<string, unknown>;
  const lineas = Array.isArray(c.lineas) ? (c.lineas as Array<Record<string, unknown>>) : [];
  const extra = Object.entries(c).filter(([k, v]) => k !== 'lineas' && v !== null && v !== undefined && v !== '');
  if (!lineas.length && !extra.length) return null;
  return (
    <ul className="space-y-0.5 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600 dark:bg-slate-900 dark:text-slate-300">
      {lineas.map((l, i) => (
        <li key={i}>
          Línea {i + 1}: {String(l.productVariantId ?? '¿?')} × {String(l.cantidad ?? 1)}
          {l.unitPrice !== undefined && l.unitPrice !== null ? ` · precio a mano ${String(l.unitPrice)}` : ''}
          {l.descuento ? ` · descuento ${String(l.descuento)}` : ''}
        </li>
      ))}
      {extra.map(([k, v]) => (
        <li key={k}>{k}: {typeof v === 'object' ? JSON.stringify(v) : String(v)}</li>
      ))}
    </ul>
  );
}

function Fila({ a, onRechazar }: { a: Aprobacion; onRechazar: (a: Aprobacion) => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const aprobar = useMutation({
    mutationFn: () => aprobacionesApi.aprobar(a.id),
    onSuccess: () => {
      toast.success('Aprobada: ya quedó hecha');
      void qc.invalidateQueries({ queryKey: ['aprobaciones'] });
    },
    onError: (err) => {
      toast.error(mensaje(err, 'No se pudo aprobar'));
      void qc.invalidateQueries({ queryKey: ['aprobaciones'] });
    },
  });
  const e = ESTADO[a.estado];
  return (
    <li className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-slate-900 dark:text-slate-100">{a.tope}</span>
          <Badge variant={e.variante}>{e.texto}</Badge>
          <Badge>{a.areaNombre}</Badge>
          {a.mia && <Badge variant="info">La pediste tú</Badge>}
        </div>
        <p className="text-sm text-slate-600 dark:text-slate-300">
          {quien(a.solicitante)}
          {a.rolDeNegocio ? ` (${a.rolDeNegocio.nombre})` : ''} {resumen(a)}
        </p>
        <Detalle a={a} />
        <p className="text-xs text-slate-500">
          {fecha(a.createdAt)}
          {a.resueltoPor && a.resueltoAt ? ` · resolvió ${quien(a.resueltoPor)}, ${fecha(a.resueltoAt)}` : ''}
          {a.estado === 'rechazada' && (a.resultadoJson as { motivo?: string } | null)?.motivo ? ` · «${(a.resultadoJson as { motivo: string }).motivo}»` : ''}
        </p>
      </div>
      {a.puedoResolver && (
        <div className="flex shrink-0 gap-2">
          <Button size="sm" variant="outline" onClick={() => onRechazar(a)} disabled={aprobar.isPending}>
            <X className="h-4 w-4" aria-hidden /> Rechazar
          </Button>
          <Button size="sm" onClick={() => aprobar.mutate()} loading={aprobar.isPending}>
            <Check className="h-4 w-4" aria-hidden /> Aprobar
          </Button>
        </div>
      )}
    </li>
  );
}

export default function Aprobaciones() {
  const [vista, setVista] = useState<'pendiente' | 'resueltas'>('pendiente');
  const [rechazando, setRechazando] = useState<Aprobacion | null>(null);
  const [motivo, setMotivo] = useState('');
  const qc = useQueryClient();
  const toast = useToast();
  const consulta = useQuery({ queryKey: ['aprobaciones', vista], queryFn: () => aprobacionesApi.listar(vista) });
  const rechazar = useMutation({
    mutationFn: () => aprobacionesApi.rechazar(rechazando!.id, motivo.trim() || undefined),
    onSuccess: () => {
      toast.success('Rechazada');
      setRechazando(null);
      setMotivo('');
      void qc.invalidateQueries({ queryKey: ['aprobaciones'] });
    },
    onError: (err) => toast.error(mensaje(err, 'No se pudo rechazar')),
  });
  const filas = consulta.data?.aprobaciones ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Aprobaciones"
        description="Lo que alguien de tu equipo pidió pasando su tope espera aquí. Al aprobarlo se hace tal cual; al rechazarlo, queda anotado."
      />
      <div role="tablist" aria-label="Qué aprobaciones ver" className="inline-flex rounded-xl border border-surface-border p-1 dark:border-slate-700">
        {(['pendiente', 'resueltas'] as const).map((v) => (
          <button
            key={v}
            role="tab"
            aria-selected={vista === v}
            onClick={() => setVista(v)}
            className={`rounded-lg px-3 py-1.5 text-sm ${vista === v ? 'bg-brand-600 text-white' : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'}`}
          >
            {v === 'pendiente' ? 'Esperando' : 'Resueltas'}
          </button>
        ))}
      </div>
      <Card>
        <CardBody className="p-0">
          {consulta.isLoading ? (
            <div className="flex justify-center p-8"><Spinner /></div>
          ) : consulta.isError ? (
            <p className="p-6 text-sm text-red-600">{mensaje(consulta.error, 'No se pudo cargar la bandeja')}</p>
          ) : filas.length === 0 ? (
            <EmptyState
              icon={ClipboardCheck}
              title={vista === 'pendiente' ? 'Nada esperando' : 'Todavía no hay resueltas'}
              description={vista === 'pendiente' ? 'Cuando alguien pase su tope, lo que pidió aparece aquí.' : 'Lo aprobado y lo rechazado queda aquí, con quién lo resolvió.'}
            />
          ) : (
            <ul className="divide-y divide-surface-border dark:divide-slate-800">
              {filas.map((a) => <Fila key={a.id} a={a} onRechazar={setRechazando} />)}
            </ul>
          )}
        </CardBody>
      </Card>
      <Modal
        open={Boolean(rechazando)}
        onClose={() => setRechazando(null)}
        title="Rechazar"
        description={rechazando ? `${quien(rechazando.solicitante)}: ${resumen(rechazando)}` : undefined}
        footer={
          <>
            <Button variant="ghost" onClick={() => setRechazando(null)}>Volver</Button>
            <Button variant="danger" onClick={() => rechazar.mutate()} loading={rechazar.isPending}>Rechazar</Button>
          </>
        }
      >
        <Input label="Por qué (opcional, lo ve quien lo pidió)" value={motivo} maxLength={300} onChange={(e) => setMotivo(e.target.value)} />
      </Modal>
    </div>
  );
}
